import ExcelJS from 'exceljs';
import { calculateFillRatio } from '../stowage/stowageOperation/stowageOperationUtils';
import { calculateVolume } from '../stowage/stowageOperation/volumeUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import Decimal from 'decimal.js';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import theme from 'src/theme';
import { prettifyDate } from 'odfjell-date-time-utils/dateTimeFormatter';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoTemperature } from '../stowage/stowageOperation/cargoTemperatureUtils';

/**
 * Returns the ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 * @param number - The number to get the suffix for
 * @returns The ordinal suffix
 */
export const getOrdinalSuffix = (number: number): string => {
    if (number % 100 >= 11 && number % 100 <= 13) {
        return 'th';
    }

    switch (number % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

// TODO: LOGIC-ERROR - The algorithm for getLetterForNumber may be incorrect for Excel column naming. Excel uses a bijective base-26 system where A=1, not A=0. The current implementation treats it as regular base-26 with A=0, which may cause incorrect column letter generation for certain ranges.
export const getLetterForNumber = (num: number): string => {
    let result = '';
    let n = num;

    while (n >= 0) {
        result = String.fromCharCode(65 + (n % 26)) + result;
        n = Math.floor(n / 26) - 1;
        if (n < 0) break;
    }

    return result;
};

// TODO: NULL-SAFETY - regex match() can return null, needs null check before destructuring
// TODO: TYPE-SAFETY - Single-digit hex values need zero-padding (e.g., 5 should be "05" not "5")
export const rgbToHex = (rgb: string): string => {
    const [r, g, b] = rgb.match(/\d+/g);
    return `${(+r).toString(16)}${(+g).toString(16)}${(+b).toString(16)}`;
};

export const setCargoColor = (cell: ExcelJS.Cell, color: string) => {
    const argbColor = `FF${color}`;

    cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: argbColor },
        bgColor: {
            argb: 'FF000000',
        },
    };
};

export const getFormattedDate = (date: Date): string => {
    return prettifyDate(date);
};

// TODO: RETURN-TYPE - Function declared to return number but can return null
// TODO: NAMING-CONVENTION - Variable _cargoTemperature uses underscore prefix, should use camelCase
export const getFormattedFillRatio = (weight: Decimal, stowagePlan: StowagePlan, cargo: CargoContent, tank: Tank): number => {
    const _cargoTemperature = getCargoTemperature(stowagePlan, cargo.cargo);

    const fillRatioString = calculateFillRatio(calculateVolume(weight, cargo.cargo, _cargoTemperature), tank).toFixed(4);
    const fillRatioNumber = parseFloat(fillRatioString);
    return fillRatioNumber > 0 ? fillRatioNumber : null;
};

export const setHeaderStyle = (cell: ExcelJS.Cell) => {
    // get color which is in rgb format (rgb(x, y, z)), change to hex format, then to argb
    const headerColorHex = rgbToHex(theme.colors.background.odfjellPanel.menuBackground);
    const headerColorArgb = `FF${headerColorHex}`;

    cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: headerColorArgb },
    };
    cell.font = {
        bold: true,
        color: { argb: 'FFFFFFFF' },
    };
    cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
    };
};

export const getCharterersText = (charterers: string[]): string => {
    const maxLength = 70;
    if (charterers && charterers.length > 0) {
        let charterersText = charterers.join(', ');
        if (charterersText.length > maxLength) {
            charterersText = charterersText.substring(0, maxLength) + '...';
        }
        return `Charterer(s): ${charterersText}`;
    }
    return '';
};
